import { Badge, Separator } from '@devizy/ui';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/quotes/$id/verify')({
  validateSearch: (search: Record<string, unknown>): { h: string } => ({
    h: typeof search.h === 'string' ? search.h : '',
  }),
  component: VerifyQuotePage,
});

const EXPORT_TYPE_LABEL: Record<string, string> = {
  DRAFT: 'Brouillon — sans valeur d’engagement',
  OFFICIAL: 'Officiel — devis consolidé',
};

function exportTypeLabel(value: string): string {
  return EXPORT_TYPE_LABEL[value] ?? value;
}

function formatDate(value: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(value);
}

function VerifyQuotePage(): React.ReactElement {
  const { id } = Route.useParams();
  const { h } = Route.useSearch();
  const { data, isLoading } = trpc.export.verifyQuote.useQuery(
    { quoteId: id, watermarkHash: h },
    { enabled: h.length > 0, retry: false },
  );

  return (
    <main className="container mx-auto max-w-xl space-y-8 px-4 py-16">
      <header className="space-y-2">
        <p className="text-primary text-xs font-semibold tracking-wide uppercase">Devizy</p>
        <h1 className="text-2xl font-semibold tracking-tight">Vérification d&apos;authenticité</h1>
        <p className="text-muted-foreground text-sm">
          Confirmez qu&apos;un devis exporté en PDF a bien été émis via devizy.app.
        </p>
      </header>

      <section className="border-border bg-card space-y-4 rounded-xl border p-6">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Vérification en cours…</p>
        ) : data ? (
          <>
            <Badge aria-label="Devis authentique">Devis authentique</Badge>
            <Separator />
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Type d&apos;export</dt>
              <dd className="font-medium">{exportTypeLabel(data.exportType)}</dd>
              <dt className="text-muted-foreground">Version du devis</dt>
              <dd className="font-mono">v{data.quoteVersion}</dd>
              <dt className="text-muted-foreground">Émis le</dt>
              <dd className="font-medium">{formatDate(new Date(data.exportedAt))}</dd>
              <dt className="text-muted-foreground">Empreinte</dt>
              <dd className="font-mono text-xs break-all">{data.watermarkHash}</dd>
            </dl>
          </>
        ) : (
          <>
            <Badge variant="outline" aria-label="Non vérifié">
              Non vérifié
            </Badge>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Aucun export officiel ne correspond à cet identifiant et cette empreinte. Le document
              n&apos;a pas pu être authentifié.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
